// CORE-REVERT (core plan item 7): the receipt of an incident or of one agent run — its runs, outbox
// rows and agent writes, each with what a revert does to it — and the one-click Revert (optional
// reason): queued sends cancelled, writes still in their 15-minute window undone, delivered sends
// marked irreversible and handed to a follow-up task. Also the per-write Undo with an optional
// reason, for the run's trail. Managers only, behind `settings.flags.core_revert`: while the
// switch is off the list answers 409 and nothing renders.
// Calls on load: `runs.revertList` or `incidents.revertList`.
import type { OperationOutput } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Textarea,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Undo2Icon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { problemText } from "../de/de-status";
import { formatDateTime } from "../format";
import { Section } from "../screen-parts";
import { applyUndo } from "../work/with-undo";
import { invalidateWork } from "../work/work-queries";

export type RevertList = OperationOutput<"runs.revertList">;
export type RevertTarget = { kind: "run" | "incident"; id: string };

export const revertKey = (co: string, t: RevertTarget) => ["revert", co, t.kind, t.id] as const;

export function revertQuery(api: Api, co: string, t: RevertTarget) {
  return {
    queryKey: revertKey(co, t),
    queryFn: (): Promise<RevertList> =>
      t.kind === "run"
        ? api.client.runs.revertList({ params: { co, id: t.id } })
        : api.client.incidents.revertList({ params: { co, id: t.id } }),
    retry: false,
  };
}

/** What a revert would do now: pure (tested). */
export function revertCounts(l: RevertList) {
  return {
    undo: l.writes.filter((w) => w.state === "undoable").length,
    cancel: l.sends.filter((s) => s.state === "cancellable").length,
    irreversible: l.sends.filter((s) => s.state === "irreversible").length,
    byHand:
      l.sends.filter((s) => s.state === "irreversible").length +
      l.writes.filter((w) => w.state === "expired").length,
  };
}

/** The Revert button's sentence: pure (tested). */
export function revertSummary(l: RevertList): string {
  const c = revertCounts(l);
  const parts = [
    c.undo ? `${c.undo} write${c.undo === 1 ? "" : "s"} undone` : null,
    c.cancel ? `${c.cancel} send${c.cancel === 1 ? "" : "s"} cancelled` : null,
    c.byHand ? `${c.byHand} left to a follow-up task` : null,
  ].filter((p): p is string => p !== null);
  return parts.length ? parts.join(", ") : "Nothing left to revert";
}

const WRITE_LABELS: Record<string, string> = {
  "task.status": "Status change",
  "task.patch": "Task edit",
  "task.field": "Field value",
  "task.move": "Move to another board",
};

const SEND_STATE: Record<
  RevertList["sends"][number]["state"],
  { label: string; v: "warning" | "neutral" | "danger" }
> = {
  cancellable: { label: "Will be cancelled", v: "warning" },
  cancelled: { label: "Cancelled", v: "neutral" },
  irreversible: { label: "Went out: by hand", v: "danger" },
};

const WRITE_STATE: Record<
  RevertList["writes"][number]["state"],
  { label: string; v: "warning" | "neutral" | "danger" }
> = {
  undoable: { label: "Will be undone", v: "warning" },
  undone: { label: "Undone", v: "neutral" },
  expired: { label: "Past undo window: by hand", v: "danger" },
};

/** Undo one agent write, with an optional reason (logged on the task and the agent's record). */
export function UndoWriteButton({
  api,
  company,
  undoId,
  onDone,
}: {
  api: Api;
  company: CompanyAccess;
  undoId: string;
  onDone?: () => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const undo = useMutation({
    mutationFn: () => applyUndo(api, company.id, undoId, reason),
    onSuccess: () => {
      toast.add({ title: "Undone", type: "success" });
      setOpen(false);
      setReason("");
      void invalidateWork(qc, company.id);
      void qc.invalidateQueries({ queryKey: ["revert", company.id] });
      onDone?.();
    },
    onError: (e) => toast.add({ title: problemText(e, "It was not undone."), type: "error" }),
  });
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <button
            type="button"
            aria-label="Undo this agent change"
            title="Undo (within 15 minutes)"
            data-slot="trail-undo"
            className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          />
        }
      >
        <Undo2Icon aria-hidden className="size-3.5" />
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-72 flex-col gap-2 p-3" aria-label="Undo">
        <Input
          aria-label="Why (optional)"
          placeholder="Why (optional)"
          maxLength={500}
          value={reason}
          onChange={(ev) => setReason(ev.target.value)}
          onKeyDown={(ev) => {
            if (ev.key === "Enter") undo.mutate();
          }}
        />
        <Button size="sm" disabled={undo.isPending} onClick={() => undo.mutate()}>
          Undo
        </Button>
      </PopoverContent>
    </Popover>
  );
}

/** The receipt of an incident or a run, with Revert. Renders nothing while the switch is off. */
export function RevertPanel({
  api,
  company,
  target,
}: {
  api: Api;
  company: CompanyAccess;
  target: RevertTarget;
}) {
  const qc = useQueryClient();
  const q = useQuery(revertQuery(api, company.id, target));
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const revert = useMutation({
    mutationFn: () => {
      const body = reason.trim() ? { reason: reason.trim() } : {};
      return target.kind === "run"
        ? api.client.runs.revert({ params: { co: company.id, id: target.id }, body })
        : api.client.incidents.revert({ params: { co: company.id, id: target.id }, body });
    },
    onSuccess: (r) => {
      const n = (o: string) => r.items.filter((i) => i.outcome === o).length;
      toast.add({
        title: `Reverted: ${n("undone")} undone, ${n("cancelled")} cancelled${
          r.follow_up_task_id ? ", the rest is in a follow-up task" : ""
        }`,
        type: "success",
      });
      setReason("");
      void invalidateWork(qc, company.id);
      void qc.invalidateQueries({ queryKey: ["revert", company.id] });
    },
    onError: (e) => toast.add({ title: problemText(e, "Nothing was reverted."), type: "error" }),
  });
  const l = q.data;
  if (!l) return null;
  if (!l.writes.length && !l.sends.length && !l.follow_up_task_id) return null;
  const c = revertCounts(l);
  const actionable = c.undo + c.cancel + c.irreversible > 0;
  return (
    <Section
      id={`revert-${target.kind}`}
      title="Revert"
      description={revertSummary(l)}
      actions={
        actionable ? (
          <Button
            size="sm"
            variant="destructive"
            disabled={revert.isPending}
            onClick={() => setOpen(true)}
            data-slot="revert-button"
          >
            {revert.isPending ? "Reverting…" : target.kind === "run" ? "Revert this run" : "Revert"}
          </Button>
        ) : null
      }
    >
      <div className="flex flex-col gap-3 text-sm" data-slot="revert-receipt">
        {l.sends.length ? (
          <ul className="flex flex-col gap-1" aria-label="Sends">
            {l.sends.map((s) => (
              <li key={s.outbox_id} className="flex min-w-0 flex-wrap items-center gap-2">
                <span className="font-medium">{s.action}</span>
                <Badge variant={SEND_STATE[s.state].v}>{SEND_STATE[s.state].label}</Badge>
                {s.sent_at ? (
                  <span className="text-xs text-muted-foreground">
                    sent {formatDateTime(s.sent_at)}
                  </span>
                ) : null}
                {s.approval_id ? (
                  <Link
                    to="/c/$slug/agents/approvals/$id"
                    params={{ slug: company.slug, id: s.approval_id }}
                    search={keepFocus}
                    className="text-xs underline-offset-4 hover:underline"
                  >
                    Approval
                  </Link>
                ) : (
                  <Link
                    to="/c/$slug/agents/outbox"
                    params={{ slug: company.slug }}
                    search={keepFocus}
                    className="text-xs underline-offset-4 hover:underline"
                  >
                    Outbox
                  </Link>
                )}
              </li>
            ))}
          </ul>
        ) : null}
        {l.writes.length ? (
          <ul className="flex flex-col gap-1" aria-label="Writes">
            {l.writes.map((w) => (
              <li key={w.undo_id} className="flex min-w-0 flex-wrap items-center gap-2">
                <span className="font-medium">{WRITE_LABELS[w.kind] ?? w.kind}</span>
                <Badge variant={WRITE_STATE[w.state].v}>{WRITE_STATE[w.state].label}</Badge>
                {w.state === "undoable" ? (
                  <span className="text-xs text-muted-foreground">
                    until {formatDateTime(w.expires_at)}
                  </span>
                ) : null}
                {w.subject.kind === "task" ? (
                  <Link
                    to="/c/$slug/work/t/$taskId"
                    params={{ slug: company.slug, taskId: w.subject.id }}
                    search={keepFocus}
                    className="text-xs underline-offset-4 hover:underline"
                  >
                    Task
                  </Link>
                ) : null}
                {w.state === "undoable" ? (
                  <UndoWriteButton api={api} company={company} undoId={w.undo_id} />
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
        {target.kind === "incident" && l.runs.length ? (
          <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            Runs held:
            {l.runs.map((r) => (
              <Link
                key={r.run_id}
                to="/c/$slug/agents/runs/$id"
                params={{ slug: company.slug, id: r.run_id }}
                search={keepFocus}
                className="underline-offset-4 hover:underline"
              >
                {r.state}
              </Link>
            ))}
          </p>
        ) : null}
        {l.follow_up_task_id ? (
          <Link
            to="/c/$slug/work/t/$taskId"
            params={{ slug: company.slug, taskId: l.follow_up_task_id }}
            search={keepFocus}
            className="text-xs text-primary-text underline-offset-4 hover:underline"
            data-slot="revert-follow-up"
          >
            Follow-up task: what is left to do by hand
          </Link>
        ) : null}
      </div>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {target.kind === "run" ? "Revert this run?" : "Revert this incident?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {revertSummary(l)}. It is logged as a reversal on the agent's record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            aria-label="Why (optional)"
            placeholder="Why (optional)"
            maxLength={500}
            value={reason}
            onChange={(ev) => setReason(ev.target.value)}
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                revert.mutate();
                setOpen(false);
              }}
            >
              Revert
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Section>
  );
}
